import { WarningCircle, CheckCircle, ArrowSquareOut, Timer, Question } from '@phosphor-icons/react';

const CONFIG = {
  healthy: {
    label: 'Reachable',
    icon: CheckCircle,
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    tooltip: 'Destination URL is reachable',
  },
  broken: {
    label: 'Broken',
    icon: WarningCircle,
    dot: 'bg-rose-500',
    chip: 'bg-rose-50 text-rose-700 border-rose-200',
    tooltip: 'Destination URL returned an error (4xx/5xx)',
  },
  timeout: {
    label: 'Timeout',
    icon: Timer,
    dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-700 border-amber-200',
    tooltip: 'Destination URL timed out',
  },
  redirect: {
    label: 'Redirects',
    icon: ArrowSquareOut,
    dot: 'bg-blue-400',
    chip: 'bg-blue-50 text-blue-700 border-blue-200',
    tooltip: 'Destination URL redirects to another URL',
  },
  unknown: null,
};

/**
 * size="sm"  → dot only
 * size="md"  → dot + label (default)
 */
export default function HealthBadge({ health, size = 'md' }) {
  const cfg = CONFIG[health];
  if (!cfg) return null;

  const Icon = cfg.icon;

  if (size === 'sm') {
    return (
      <span className="relative group">
        <span className={`inline-flex h-2 w-2 rounded-full ${cfg.dot}`} />
        <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-foreground px-2 py-1 text-[10px] font-medium text-background opacity-0 group-hover:opacity-100 transition-opacity">
          {cfg.tooltip}
        </span>
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${cfg.chip}`}>
      <Icon size={10} weight="fill" />
      {cfg.label}
    </span>
  );
}

export function HealthBadgeLarge({ health, httpStatus, checkedAt }) {
  const cfg = CONFIG[health];
  if (!cfg) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-border/80 bg-muted/20 px-4 py-3">
        <Question size={20} className="text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Not yet checked</span>
      </div>
    );
  }

  const Icon = cfg.icon;
  return (
    <div className={`flex items-center justify-between rounded-xl border px-4 py-3 ${cfg.chip}`}>
      <div className="flex items-center gap-3">
        <Icon size={22} weight="fill" />
        <div>
          <p className="text-sm font-bold capitalize">{health}</p>
          <p className="text-xs opacity-80">{cfg.tooltip}</p>
        </div>
      </div>
      <div className="text-right">
        {httpStatus && (
          <span className="text-sm font-mono font-bold">{httpStatus}</span>
        )}
        {checkedAt && (
          <p className="text-[10px] opacity-70 mt-0.5">
            Checked {new Date(checkedAt).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}
